/**
 * Admin Organizations List Page
 *
 * Searchable list of all organizations with quick actions.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { AdminPageHeader, AdminCard } from '@/components/admin/AdminShell';
import { CreateOrgModal } from '@/components/admin/CreateOrgModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Building2,
  Search,
  Plus,
  MoreVertical,
  Eye,
  UserPlus,
  Settings,
  Shield,
  Loader2,
} from 'lucide-react';
import { Org, OrgEntitlements } from '@/integrations/firebase/types';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { useToast } from '@/hooks/use-toast';

interface OrgWithEntitlements extends Org {
  entitlements?: OrgEntitlements;
}

export default function AdminOrgs() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canImpersonate } = useAdminAuth();
  const { enterSupportMode } = useSupportMode();
  const { toast } = useToast();

  const [orgs, setOrgs] = useState<OrgWithEntitlements[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Fetch orgs
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        setLoading(true);

        // Fetch orgs
        const orgsSnapshot = await getDocs(
          query(collection(db, 'orgs'), orderBy('createdAt', 'desc'))
        );

        const orgsData: OrgWithEntitlements[] = [];

        for (const doc of orgsSnapshot.docs) {
          const orgData = { id: doc.id, ...doc.data() } as Org;
          orgsData.push(orgData);
        }

        // Fetch entitlements in parallel
        const entitlementsSnapshot = await getDocs(collection(db, 'org_entitlements'));
        const entitlementsMap = new Map<string, OrgEntitlements>();
        entitlementsSnapshot.docs.forEach((doc) => {
          entitlementsMap.set(doc.id, doc.data() as OrgEntitlements);
        });

        // Merge entitlements
        orgsData.forEach((org) => {
          org.entitlements = entitlementsMap.get(org.id);
        });

        setOrgs(orgsData);
      } catch (error) {
        console.error('Error fetching orgs:', error);
        toast({
          title: 'Error',
          description: 'Failed to load organizations',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrgs();
  }, [toast]);

  // Filter orgs
  const filteredOrgs = useMemo(() => {
    return orgs.filter((org) => {
      // Skip orgs without required fields
      if (!org.name) {
        console.warn(`Org ${org.id} is missing name field`);
        return false;
      }

      // Status filter
      if (statusFilter !== 'all' && org.status !== statusFilter) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          org.name.toLowerCase().includes(query) ||
          (org.primaryContactEmail || '').toLowerCase().includes(query) ||
          org.id.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [orgs, searchQuery, statusFilter]);

  const handleImpersonate = async (org: Org) => {
    const result = await enterSupportMode(org.id);
    if (result.success) {
      toast({
        title: 'Support Mode Activated',
        description: `Now acting as ${org.name || org.id}`,
      });
      navigate(`/admin/orgs/${org.id}`);
    } else {
      toast({
        title: 'Failed to Enter Support Mode',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '—';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div>
      <AdminPageHeader
        title="Organizations"
        description="Manage customer organizations and their settings"
        actions={
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Organization
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            type="text"
            placeholder="Search by name, email, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[hsl(220,20%,12%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
          />
        </div>

        <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
          <SelectTrigger className="w-40 bg-[hsl(220,20%,12%)] border-[hsl(220,15%,25%)] text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orgs Table */}
      <AdminCard className="p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          </div>
        ) : filteredOrgs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {searchQuery || statusFilter !== 'all'
              ? 'No organizations match your filters'
              : 'No organizations yet'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[hsl(220,15%,20%)] hover:bg-transparent">
                <TableHead className="text-gray-400">Organization</TableHead>
                <TableHead className="text-gray-400">Plan</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400">Created</TableHead>
                <TableHead className="text-gray-400 w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrgs.map((org) => (
                <TableRow
                  key={org.id}
                  className="border-[hsl(220,15%,20%)] hover:bg-[hsl(220,15%,12%)] cursor-pointer"
                  onClick={() => navigate(`/admin/orgs/${org.id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-[hsl(220,15%,20%)] flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{org.name}</p>
                        <p className="text-xs text-gray-500">{org.primaryContactEmail}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="border-[hsl(220,15%,30%)] text-gray-300 capitalize"
                    >
                      {org.entitlements?.planTier || 'starter'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        org.status === 'active'
                          ? 'border-green-500/50 text-green-400'
                          : 'border-red-500/50 text-red-400'
                      }
                    >
                      {org.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-400">
                    {formatDate(org.createdAt)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-white hover:bg-[hsl(220,15%,20%)]"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]"
                      >
                        <DropdownMenuItem
                          onClick={() => navigate(`/admin/orgs/${org.id}`)}
                          className="text-gray-300 focus:text-white focus:bg-[hsl(220,15%,20%)]"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate(`/admin/orgs/${org.id}?tab=users`)}
                          className="text-gray-300 focus:text-white focus:bg-[hsl(220,15%,20%)]"
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Manage Users
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate(`/admin/orgs/${org.id}?tab=scope`)}
                          className="text-gray-300 focus:text-white focus:bg-[hsl(220,15%,20%)]"
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Edit Scope
                        </DropdownMenuItem>
                        {canImpersonate && (
                          <>
                            <DropdownMenuSeparator className="bg-[hsl(220,15%,25%)]" />
                            <DropdownMenuItem
                              onClick={() => handleImpersonate(org)}
                              className="text-amber-400 focus:text-amber-300 focus:bg-[hsl(220,15%,20%)]"
                            >
                              <Shield className="h-4 w-4 mr-2" />
                              Enter Support Mode
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminCard>

      {/* Create Org Modal */}
      <CreateOrgModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSuccess={() => {
          setShowCreateModal(false);
          // Refresh list
          window.location.reload();
        }}
      />
    </div>
  );
}
